import { AvertissementDroitsImage } from '../../composants/AvertissementDroitsImage.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { CibleNewsletter } from './CibleNewsletter.js';
import { Badge, Button } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { texteEnLangue } from '@artosera/shared';
import { useOeuvres } from '../../api/oeuvres.js';
import { useExpositions } from '../../api/expositions.js';
import { useVisuels, useCreerVisuel, useTelechargerVisuel } from '../../api/newsletter.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ErreurNewsletter } from './ErreurNewsletter.js';
import styles from './NewsletterPage.module.css';
export function VisuelsTab() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const langue = i18n.language.startsWith('en') ? 'en' : 'fr';
  const oeuvres = useOeuvres(galerieSlug, { statut: ['disponible'], vignettes: true });
  const expositions = useExpositions(galerieSlug);
  const visuels = useVisuels(galerieSlug);
  const creer = useCreerVisuel(galerieSlug);
  const telecharger = useTelechargerVisuel(galerieSlug);
  const [cible, setCible] = useState('');
  const [typeCible, idCible] = cible.split(':');
  const oeuvreCible =
    typeCible === 'oeuvre'
      ? idCible
      : expositions.data?.expositions.find((e) => e.id === idCible)?.oeuvres[0];
  const ficheCible = useOeuvre(galerieSlug, oeuvreCible ?? null);
  const mediasCible =
    ficheCible.data?.medias.filter((m) => m.id === ficheCible.data.oeuvre.mediaPrincipalId) ?? [];
  const [format, setFormat] = useState<'carre' | 'vertical'>('carre');
  return (
    <section className={styles.section} data-ui="newsletter-visuals">
      {galerieALaction(galerie, 'newsletter:write') ? (
        <form
          className={styles.formulaire}
          onSubmit={(e) => {
            e.preventDefault();
            const [type, id] = cible.split(':');
            if ((type === 'oeuvre' || type === 'exposition') && id)
              creer.mutate({ cible: { type, id }, format });
          }}
        >
          <label>
            {t('regie:newsletter.visuels.cible')}
            <select required value={cible} onChange={(e) => setCible(e.target.value)}>
              <option value="">{t('regie:newsletter.visuels.choisir')}</option>
              <optgroup label={t('regie:newsletter.visuels.oeuvres')}>
                {oeuvres.data?.oeuvres.map((o) => (
                  <option key={o.id} value={`oeuvre:${o.id}`}>
                    {o.artisteNom} · {o.titre}
                  </option>
                ))}
              </optgroup>
              <optgroup label={t('regie:newsletter.visuels.expositions')}>
                {expositions.data?.expositions.map((e) => (
                  <option key={e.id} value={`exposition:${e.id}`}>
                    {texteEnLangue(e.titre, langue)}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          <fieldset>
            <legend>{t('regie:newsletter.visuels.format')}</legend>
            {(['carre', 'vertical'] as const).map((f) => (
              <label key={f} className={styles.case}>
                <input
                  type="radio"
                  name="format-visuel"
                  value={f}
                  checked={format === f}
                  onChange={() => setFormat(f)}
                />
                {t(`regie:newsletter.visuels.${f}`)}
              </label>
            ))}
          </fieldset>
          <AvertissementDroitsImage medias={mediasCible} usages={['reseaux', 'presse']} />
          <Button type="submit" disabled={creer.isPending || ficheCible.isLoading || !cible}>
            {t('regie:newsletter.visuels.creer')}
          </Button>
          <ErreurNewsletter erreur={creer.error ?? ficheCible.error} />
        </form>
      ) : null}
      <ErreurNewsletter erreur={visuels.error} />
      <ErreurNewsletter erreur={telecharger.error} />
      <div className={styles.grille}>
        {visuels.data?.visuels.map((v) => {
          const oeuvre =
            v.cible.type === 'oeuvre'
              ? oeuvres.data?.oeuvres.find((o) => o.id === v.cible.id)
              : null;
          const expo =
            v.cible.type === 'exposition'
              ? expositions.data?.expositions.find((e) => e.id === v.cible.id)
              : null;
          return (
            <article className={styles.carte} key={v.id}>
              <CibleNewsletter
                cible={v.cible}
                oeuvre={oeuvre}
                exposition={expo}
                url={oeuvre ? oeuvres.data?.vignettes?.urls[oeuvre.id] : null}
                langue={langue}
                format={v.format}
              />
              <p>
                <Badge>{t(`regie:newsletter.visuels.etats.${v.etat}`)}</Badge> ·{' '}
                {t(`regie:newsletter.visuels.${v.format}`)}
              </p>
              {v.etat === 'en_attente' ? (
                <p aria-live="polite">{t('regie:newsletter.visuels.enCours')}</p>
              ) : null}
              {v.etat === 'pret' ? (
                <Button
                  variant="secondary"
                  disabled={telecharger.isPending}
                  onClick={() => {
                    void telecharger
                      .mutateAsync(v.id)
                      .then((r) => {
                        ouvrirUrlSignee(r.telechargement.url);
                      })
                      .catch(() => {});
                  }}
                >
                  {t('regie:newsletter.visuels.telecharger')}
                </Button>
              ) : null}
            </article>
          );
        })}
      </div>
      {visuels.data && !visuels.data.visuels.length ? (
        <p>{t('regie:newsletter.visuels.vide')}</p>
      ) : null}
    </section>
  );
}
